/**
 * Space invite validity rules: expiry windows, use caps, and temporary access
 * durations.
 *
 * "Validity" is about the invite itself — whether it can still be accepted or
 * redeemed. That is independent of {@link SPACE_TEMPORARY_ACCESS_DURATIONS},
 * which governs how long a member keeps access *after* joining.
 *
 * Pure and shared so the create UI, request validation, and the API compute
 * the same instants from the same presets.
 *
 * @module constants/space-invite-rules
 */
/** How long a new invite (direct or link) stays usable. */
export const SPACE_INVITE_EXPIRY_OPTIONS = [
    '30m',
    '1h',
    '6h',
    '12h',
    '1d',
    '7d',
    '30d',
    'never',
];
/** Applied when the creator does not choose an expiry. */
export const SPACE_INVITE_DEFAULT_EXPIRY = '7d';
/** How long a temporary member keeps access after joining. */
export const SPACE_TEMPORARY_ACCESS_DURATIONS = ['30m', '1h', '6h', '1d', '7d', '30d'];
/**
 * Temporary access kinds. Only `duration` is honoured today; `until_event` is
 * a reserved slot so stored documents and payloads do not need reshaping when
 * event-driven expiry lands.
 */
export const SPACE_TEMPORARY_ACCESS_KINDS = ['duration', 'until_event'];
/** Use-cap presets offered in the create-link UI. `null` means unlimited. */
export const SPACE_INVITE_MAX_USES_PRESETS = [1, 5, 10, 25, 50, 100];
/** Hard ceiling on a link's use cap, so a cap cannot be used as a counter. */
export const SPACE_INVITE_MAX_USES_LIMIT = 1000;
/** Bytes of entropy in an invite link code (base64url encoded). */
export const SPACE_INVITE_CODE_BYTES = 16;
/**
 * Encoded length of a code with {@link SPACE_INVITE_CODE_BYTES} of entropy.
 * base64url of 16 bytes is 22 chars once padding is stripped.
 */
export const SPACE_INVITE_CODE_LENGTH = 22;
/** Codes are base64url with padding stripped. */
export const SPACE_INVITE_CODE_PATTERN = /^[A-Za-z0-9_-]{22}$/;
/**
 * Rolling window for direct-invite anti-spam: per-target cooldown (Spaces and
 * group DMs) and the Space-wide quota.
 */
export const INVITE_PER_TARGET_WINDOW_MS = 24 * 60 * 60 * 1000;
export const INVITE_PER_TARGET_WINDOW_HOURS = 24;
/** Max same-offer direct invites to one identity in {@link INVITE_PER_TARGET_WINDOW_MS}. */
export const INVITE_PER_TARGET_LIMIT = 2;
/** Space-wide direct-invite quota is this fraction of current member count. */
export const SPACE_DIRECT_INVITE_QUOTA_RATIO = 0.05;
export const SPACE_DIRECT_INVITE_QUOTA_FLOOR = 10;
export const SPACE_DIRECT_INVITE_QUOTA_CEILING = 100;
const DURATION_MS = {
    '30m': 30 * 60 * 1000,
    '1h': 60 * 60 * 1000,
    '6h': 6 * 60 * 60 * 1000,
    '12h': 12 * 60 * 60 * 1000,
    '1d': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
};
/**
 * Absolute expiry for a new invite. `never` yields `null` (no expiry).
 * An omitted choice falls back to {@link SPACE_INVITE_DEFAULT_EXPIRY}.
 */
export function spaceInviteExpiresAt(expiry, from = new Date()) {
    const choice = expiry ?? SPACE_INVITE_DEFAULT_EXPIRY;
    if (choice === 'never')
        return null;
    return new Date(from.getTime() + DURATION_MS[choice]);
}
/** Absolute expiry for a temporary membership granted by an invite. */
export function spaceTemporaryAccessExpiresAt(spec, from = new Date()) {
    if (!spec || spec.kind !== 'duration')
        return null;
    return new Date(from.getTime() + DURATION_MS[spec.duration]);
}
/** Whether a use cap is a value we will store. */
export function isValidSpaceInviteMaxUses(maxUses) {
    if (maxUses == null)
        return true;
    return Number.isInteger(maxUses) && maxUses >= 1 && maxUses <= SPACE_INVITE_MAX_USES_LIMIT;
}
/**
 * Whether an invite or link can still be redeemed.
 *
 * Callers must treat every falsy result identically on unauthenticated
 * surfaces: distinguishing "expired" from "revoked" from "unknown code" leaks
 * the existence of a hidden Space to whoever is probing codes.
 */
export function isSpaceInviteUsable(invite, now = new Date()) {
    if (invite.status !== 'pending' && invite.status !== 'active')
        return false;
    if (invite.expiresAt != null) {
        const expiresAt = invite.expiresAt instanceof Date ? invite.expiresAt : new Date(invite.expiresAt);
        if (expiresAt.getTime() <= now.getTime())
            return false;
    }
    if (invite.maxUses != null && (invite.useCount ?? 0) >= invite.maxUses)
        return false;
    return true;
}
function nonNegativeInt(n) {
    if (!Number.isFinite(n))
        return 0;
    return Math.max(0, Math.floor(n));
}
/**
 * How many direct invites a Space may send in {@link INVITE_PER_TARGET_WINDOW_MS}.
 * `floor(members * 5%)`, then clamped to 10…100.
 */
export function spaceDirectInviteQuotaLimit(memberCount) {
    const members = nonNegativeInt(memberCount);
    return Math.min(SPACE_DIRECT_INVITE_QUOTA_CEILING, Math.max(SPACE_DIRECT_INVITE_QUOTA_FLOOR, Math.floor(members * SPACE_DIRECT_INVITE_QUOTA_RATIO)));
}
/** Used / limit / remaining for the live member count. */
export function spaceDirectInviteQuota(used, memberCount) {
    const limit = spaceDirectInviteQuotaLimit(memberCount);
    const usedCount = nonNegativeInt(used);
    return {
        used: usedCount,
        limit,
        remaining: Math.max(0, limit - usedCount),
        memberCount: nonNegativeInt(memberCount),
        windowHours: INVITE_PER_TARGET_WINDOW_HOURS,
    };
}
/**
 * Stable comparison of an invite's membership grants. Expiry is omitted —
 * changing how long the invite stays open is not a permission/scope fix.
 */
export function spaceInviteGrantFingerprint(grants) {
    const roles = [...new Set((grants.grantedRoleIds ?? []).map(String))].sort();
    const channels = [...new Set((grants.grantedChannelIds ?? []).map(String))].sort();
    const temp = grants.temporaryAccess
        ? `${grants.temporaryAccess.kind}:${grants.temporaryAccess.duration}`
        : '';
    return JSON.stringify({ roles, channels, temp });
}
/**
 * Whether another direct invite to the same identity is allowed in the window.
 * After {@link INVITE_PER_TARGET_LIMIT} sends, only a different grant fingerprint
 * (roles / channels / temporary access) may go out.
 */
export function spaceInviteTargetCooldownAllows(args) {
    const limit = args.limit ?? INVITE_PER_TARGET_LIMIT;
    if (args.recentCount < limit)
        return true;
    if (args.latestFingerprint == null)
        return false;
    return args.nextFingerprint !== args.latestFingerprint;
}
//# sourceMappingURL=space-invite-rules.js.map